
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AppRole } from "@/types/database/auth";
import { useToast } from "@/hooks/use-toast";

export const useUserRoles = (userId?: string) => {
  const [role, setRole] = useState<AppRole>("user");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserRole = async () => {
      let userIdToUse = userId;
      
      if (!userIdToUse) {
        // If no userId provided, try to get current auth user
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          setLoading(false);
          return;
        }
        userIdToUse = session.user.id;
      }

      try {
        console.log("Fetching role for user:", userIdToUse);
        
        // Use the RPC function to get the user role
        const { data, error } = await supabase
          .rpc('get_user_role_safely', {
            user_id_input: userIdToUse
          });

        if (error) {
          console.error("Error fetching user role:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to fetch user role. Please try refreshing the page.",
          });
          return;
        }

        console.log("Role data received:", data);
        if (data) {
          setRole(data as AppRole);
        }
      } catch (error) {
        console.error("Error in fetchUserRole:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred while fetching user role",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [userId, toast]);

  const updateUserRole = async (userId: string, newRole: AppRole) => {
    try {
      console.log("Updating role for user:", userId, "to:", newRole);
      
      const { error } = await supabase
        .rpc('update_user_role_safely', {
          user_id_input: userId,
          new_role: newRole
        });

      if (error) throw error;

      setRole(newRole);
      toast({
        title: "Success",
        description: "User role updated successfully",
      });

      return { success: true, error: null };
    } catch (error) {
      console.error("Error updating user role:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update user role",
      });
      return { success: false, error };
    }
  };

  return { role, loading, updateUserRole };
};
